import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useCreateGatewayPricingPlan } from 'src/features/gateway/api/useCreateGatewayPricingPlan';
import { LIST_GATEWAY_PRICING_PLANS_RESOURCE } from 'src/features/gateway/api/useListGatewayPricingPlans';
import GatewayPricingPlanFormModal from 'src/features/gateway/components/form/GatewayPricingPlanFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
}

export default function CreateGatewayPricingPlanFormModal<TData>({
    className,
    semantics,
}: PricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { gatewayId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateGatewayPricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAY_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccess),
                description: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <GatewayPricingPlanFormModal
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ ...data, gateway: gatewayId, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.addPricingPlanSubtitle)}
        />
    );
}
